import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import OrcamentoMensalCard from '../../components/dashboard/OrcamentoMensalCard'
import SafetyStockCard from '../../components/dashboard/SafetyStockCard'
import { useCompras } from '../../hooks/useCompras'
import { useReplenishment } from '../../hooks/useReplenishment'
import { useCompanyBudget } from '../../hooks/useCompanyBudget'
import { useCostOfGoods, costRealByMonth } from '../../hooks/useCostOfGoods'
import { useToast } from '../../hooks/useToast'
import { supabase } from '../../lib/supabase'
import { fmtBRL } from '../../lib/format'
import { gerarPlanoDeCompra, partitionAutoApproval, type PlanoGrupo, type PlanoItem } from '../../lib/planoDeCompra'
import { aplicarDobraRuptura, isRupturaAcionavel } from '../../lib/replenishment'
import { explainPlanoItem, CHIP_TONE_CLASS } from '../../lib/planExplain'
import { useAutoApproval, setAutoApproval } from '../../lib/flags'
import { buildWhatsAppNegotiationLink } from '../../lib/orderShare'
import { usePurchaseOrders } from '../../hooks/usePurchaseOrders'
import { forecastRupturaElimination, RUPTURA_TARGET } from '../../lib/rupturaForecast'
import { buildRupturaStrategy, plannedOrdersFromGroups } from '../../lib/rupturaStrategy'
import { computeReforcoRuptura } from '../../lib/reforcoRuptura'
import RupturaForecastCard from '../../components/purchase/RupturaForecastCard'
import PurchaseExplorer from '../../components/purchase/PurchaseExplorer'
import MiguelChatDrawer from '../../components/purchase/MiguelChatDrawer'
import { buildPanelSnapshot } from '../../lib/panelSnapshot'
import { useIsMobile } from '../../hooks/useIsMobile'

const PRIORITY_BADGE: Record<string, string> = {
  Zerado:  'bg-[#7030A0] text-white',
  Baixo:   'bg-[#FF0000] text-white',
  Comprar: 'bg-[#00B0F0] text-gray-900',
}

const fmtInt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })

export default function MiguelCompradorPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { notify } = useToast()
  const { rows, loading } = useReplenishment()
  const { orders } = useCompras()
  // Pedidos com itens+qty (a projeção precisa das quantidades; useCompras traz só ids).
  const { orders: poWithItems } = usePurchaseOrders()
  const { budget } = useCompanyBudget()
  const { entries: cogEntries } = useCostOfGoods()
  const [qtyOverride, setQtyOverride] = useState<Record<string, number>>({})
  const [showOverflows, setShowOverflows] = useState(false)
  const [busy, setBusy] = useState(false)
  // Sub-abas: "plano" (estratégia + plano automático) e "explorar" (curadoria
  // manual com busca/IA/filtros — ex-aba Sugestão, agora embutida aqui).
  const [view, setView] = useState<'plano' | 'explorar'>('plano')
  // Navegação mobile por drill-in: hub de cards → kanban ou estratégia.
  const [mobileNav, setMobileNav] = useState<'hub' | 'kanban' | 'estrategia'>('hub')
  const monthKey = new Date().toISOString().slice(0, 7)
  const today = useMemo(() => new Date(), [])

  // Aplica overrides de qty mantendo as linhas originais. O override ajusta
  // total_suggestion (matriz + filial), que é o que vira o pedido emitido.
  const rowsWithOverrides = useMemo(() => {
    return rows.map(r => {
      const ov = qtyOverride[r.id]
      // Ajuste manual do usuário sempre vence (mesmo igual à base).
      if (ov != null) return ov === r.total_suggestion ? r : { ...r, total_suggestion: Math.max(0, Math.floor(ov)) }
      // Sem ajuste: itens em ruptura entram com o DOBRO (anti-re-ruptura no lead time).
      const dobrada = aplicarDobraRuptura(r.total_suggestion, r.current_stock, r.kanban_status)
      return dobrada === r.total_suggestion ? r : { ...r, total_suggestion: dobrada }
    })
  }, [rows, qtyOverride])

  // Snapshot agregado do painel para o chat IA (contexto — nunca linha bruta).
  const panelSnapshot = useMemo(
    () => buildPanelSnapshot(rows, poWithItems, budget),
    [rows, poWithItems, budget],
  )

  // Cap por Custo de Mercadoria: custo real lançado por mês (base matriz). A folga
  // de cada mês = teto − custo real; mês sem lançamento ⇒ 0 (teto cheio).
  const custoRealMes = useMemo(() => Object.fromEntries(costRealByMonth(cogEntries)), [cogEntries])

  const plano = useMemo(() => gerarPlanoDeCompra({
    rows: rowsWithOverrides,
    budgetMonthly: budget.purchase_budget_monthly,
    cutoffDay: budget.purchase_cutoff_day,
    today,
    monthKey,
    costRealByMonth: custoRealMes,
  }), [rowsWithOverrides, budget, monthKey, today, custoRealMes])

  const capAtivo = budget.purchase_budget_monthly > 0
  const totalItens = plano.fits.reduce((s, g) => s + g.rows.length, 0)
  const totalOverflowItens = plano.overflows.reduce((s, g) => s + g.rows.length, 0)

  // Reforço de ruptura: itens zerados com pedido ENVIADO sem o dobro (transitório —
  // pré-regra do dobro). Usa rows originais (não os dobrados). Só diagnóstico.
  const reforco = useMemo(() => computeReforcoRuptura(rows), [rows])

  // Estratégia anti-ruptura (meta ≤5%): projeta o índice com os pedidos a caminho
  // (baseline) vs. com o plano do Miguel (fits) vs. com o plano + além do teto.
  // Reusa a mesma projeção da aba Pedidos, tratando o plano como pedidos planejados.
  const rupturaStrategy = useMemo(() => {
    const fcRows = rows.map(r => ({
      id: r.id, current_stock: r.current_stock, kanban_status: r.kanban_status, monthly_demand: r.demanda_prevista,
      reorder_point: r.reorder_point, lead_time: r.suppliers?.lead_time_days ?? null,
    }))
    // baseline = "se nada mais for comprado" (single-shot). com plano = reposição contínua.
    const baseline = forecastRupturaElimination({ rows: fcRows, orders: poWithItems })
    const withFits = forecastRupturaElimination({
      rows: fcRows, orders: [...poWithItems, ...plannedOrdersFromGroups(plano.fits)], replenish: true,
    })
    const withAll = forecastRupturaElimination({
      rows: fcRows, orders: [...poWithItems, ...plannedOrdersFromGroups([...plano.fits, ...plano.overflows])], replenish: true,
    })
    const strat = buildRupturaStrategy({
      baseline, withFits, withFitsAndOverflows: withAll,
      rows: rows.map(r => ({
        current_stock: r.current_stock, kanban_status: r.kanban_status,
        has_supplier: !!r.suppliers, lead_time: r.suppliers?.lead_time_days ?? null,
      })),
      fitsCount: totalItens, overflowCount: totalOverflowItens, overflowValue: plano.totalOverflows,
    })
    return { baseline, withFits, strat }
  }, [rows, poWithItems, plano, totalItens, totalOverflowItens])

  // Fase 3: aprovação por exceção (sob flag bx:autoAprovacao, default OFF).
  // Divide os itens do plano em seguros (Miguel aprova) e exceções (revisão humana).
  const autoApproval = useAutoApproval()
  const autoPartition = useMemo(
    () => (autoApproval ? partitionAutoApproval(plano.fits) : null),
    [autoApproval, plano.fits],
  )
  const segurosCount = autoPartition?.seguros.length ?? 0
  const excecoesCount = autoPartition?.excecoes.length ?? 0

  // Estratégia <5%: aprova os itens ACIMA do teto (overflows) quando são
  // necessários para a meta — decisão consciente de caixa vs ruptura.
  async function aprovarAlemDoTeto() {
    const items = plano.overflows.flatMap(g => g.rows)
    if (items.length === 0) return
    if (!confirm(`Aprovar ${items.length} ${items.length === 1 ? 'item' : 'itens'} ACIMA do teto do mês? Ficam disponíveis em Emissão de Pedidos.`)) return
    setBusy(true)
    let ok = 0, fail = 0
    for (const r of items) {
      const qty = Math.floor(r.total_suggestion)
      if (qty <= 0) continue
      const { error } = await supabase.from('products').update({
        approval_input: String(qty), approved_qty: qty, kanban_status: 'Compra Aprovada',
      }).eq('id', r.id)
      if (error) fail++; else ok++
    }
    setBusy(false)
    notify(fail === 0 ? 'success' : 'error', `${ok} itens (acima do teto) aprovados${fail > 0 ? `; ${fail} falharam` : ''}.`)
  }

  async function aprovarTodos() {
    if (totalItens === 0) { notify('error', 'Nenhum item no plano.'); return }
    if (!confirm(`Aprovar ${totalItens} itens em ${plano.fits.length} fornecedores? Eles ficam disponíveis em Emissão de Pedidos.`)) return
    setBusy(true)
    const itemsToApprove = plano.fits.flatMap(g => g.rows)
    let ok = 0, fail = 0
    for (const r of itemsToApprove) {
      // Aprovamos o total_suggestion (matriz + filial) — o pedido emitido
      // cobre o que matriz repõe + o que distribuirá às filiais via Separação.
      const qty = Math.floor(r.total_suggestion)
      if (qty <= 0) continue
      const { error } = await supabase.from('products').update({
        approval_input: String(qty),
        approved_qty: qty,
        kanban_status: 'Compra Aprovada',
      }).eq('id', r.id)
      if (error) fail++; else ok++
    }
    setBusy(false)
    if (fail === 0) {
      notify('success', `${ok} itens aprovados. Indo para Emissão de Pedidos...`)
      setTimeout(() => navigate('/relatorio-compras'), 800)
    } else {
      notify('error', `${ok} aprovados, ${fail} falharam.`)
    }
  }

  async function postergarOverflows() {
    if (totalOverflowItens === 0) return
    if (!confirm(`Marcar ${totalOverflowItens} itens como Standby para a virada do mês?`)) return
    setBusy(true)
    const itemsToStandby = plano.overflows.flatMap(g => g.rows)
    let ok = 0, fail = 0
    for (const r of itemsToStandby) {
      const { error } = await supabase.from('products').update({
        approval_input: 'S',
        approved_qty: 0,
        kanban_status: 'Standby',
      }).eq('id', r.id)
      if (error) fail++; else ok++
    }
    setBusy(false)
    notify(fail === 0 ? 'success' : 'error', `${ok} itens marcados como Standby${fail > 0 ? `; ${fail} falharam` : ''}.`)
  }

  // Fase 3: aprova só os itens SEGUROS (Miguel decide); exceções ficam para revisão.
  async function aprovarSeguros() {
    const items = autoPartition?.seguros ?? []
    if (items.length === 0) { notify('error', 'Nenhum item seguro para aprovar.'); return }
    if (!confirm(`Aprovar ${items.length} itens seguros? As ${excecoesCount} exceções continuam para sua revisão.`)) return
    setBusy(true)
    let ok = 0, fail = 0
    for (const r of items) {
      const qty = Math.floor(r.total_suggestion)
      if (qty <= 0) continue
      const { error } = await supabase.from('products').update({
        approval_input: String(qty), approved_qty: qty, kanban_status: 'Compra Aprovada',
      }).eq('id', r.id)
      if (error) fail++; else ok++
    }
    setBusy(false)
    notify(fail === 0 ? 'success' : 'error',
      fail === 0
        ? `${ok} itens seguros aprovados. Revise as exceções ou vá para Emissão de Pedidos.`
        : `${ok} aprovados, ${fail} falharam.`)
  }

  // Ações individuais para itens em exceção (o realtime do useReplenishment atualiza a lista).
  async function aprovarUm(r: PlanoItem) {
    const qty = Math.floor(r.total_suggestion)
    if (qty <= 0) { notify('error', 'Quantidade zero.'); return }
    setBusy(true)
    const { error } = await supabase.from('products').update({
      approval_input: String(qty), approved_qty: qty, kanban_status: 'Compra Aprovada',
    }).eq('id', r.id)
    setBusy(false)
    notify(error ? 'error' : 'success', error ? `Erro: ${error.message}` : `${r.name}: aprovado (${qty}).`)
  }
  async function standbyUm(r: PlanoItem) {
    setBusy(true)
    const { error } = await supabase.from('products').update({
      approval_input: 'S', approved_qty: 0, kanban_status: 'Standby',
    }).eq('id', r.id)
    setBusy(false)
    notify(error ? 'error' : 'success', error ? `Erro: ${error.message}` : `${r.name}: em standby.`)
  }

  // Fase 4 (negociação assistida): fornecedor recusou a condição → adia o grupo
  // inteiro para a virada (Standby). É o "reagenda automaticamente" do fluxo.
  async function adiarGrupo(g: PlanoGrupo) {
    if (!confirm(`Adiar ${g.rows.length} ${g.rows.length === 1 ? 'item' : 'itens'} de ${g.fornecedor_nome} para a virada (Standby)?`)) return
    setBusy(true)
    let ok = 0, fail = 0
    for (const r of g.rows) {
      const { error } = await supabase.from('products').update({
        approval_input: 'S', approved_qty: 0, kanban_status: 'Standby',
      }).eq('id', r.id)
      if (error) fail++; else ok++
    }
    setBusy(false)
    notify(fail === 0 ? 'success' : 'error', `${ok} itens de ${g.fornecedor_nome} adiados para a virada${fail > 0 ? `; ${fail} falharam` : ''}.`)
  }

  return (
    <AppLayout title="Comprador">
      <div className="space-y-4">
        {/* Cabeçalho */}
        <div className="bg-surface border border-line rounded-2xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="text-3xl">🛒</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-accent text-brand-900 px-2 py-0.5 rounded-full">📋 Plano de Compra</span>
                <label className="ml-auto flex items-center gap-1.5 text-[11px] text-ink-muted cursor-pointer select-none" title="Beta: o Miguel aprova os itens seguros e separa as exceções (acima do teto, sem histórico, baixa qualidade comercial ou mínimo do fornecedor não atingido) para sua revisão.">
                  <input
                    type="checkbox"
                    checked={autoApproval}
                    onChange={e => setAutoApproval(e.target.checked)}
                    className="accent-brand-600"
                  />
                  🧪 Aprovação por exceção (beta)
                </label>
              </div>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-ink mt-1.5">Comprador</h1>
              <p className="text-sm text-ink-muted mt-1 leading-snug">
                Pedido pronto pra emitir — respeitando seu cap mensal, prazos do fornecedor e lead time.
                Só entram itens com estoque <strong>Zerado</strong>, <strong>Baixo</strong> ou <strong>Comprar</strong> (do Painel BX).
              </p>
            </div>
          </div>
        </div>

        {/* MOBILE: hub de cards (Kanban + Estratégia) com drill-in */}
        {isMobile && mobileNav === 'hub' && (
          <CompradorHub
            plano={plano}
            orders={poWithItems}
            rows={rows}
            headline={rupturaStrategy.strat.headline}
            total={plano.totalFits}
            onAprovar={aprovarTodos}
            onOpenKanban={() => setMobileNav('kanban')}
            onOpenEstrategia={() => setMobileNav('estrategia')}
            busy={busy}
          />
        )}
        {isMobile && mobileNav === 'kanban' && (
          <KanbanScreen
            plano={plano}
            orders={poWithItems}
            rows={rows}
            onBack={() => setMobileNav('hub')}
            onOpenPedidos={() => navigate('/historico-pedidos')}
            onOpenEmissao={() => navigate('/relatorio-compras')}
          />
        )}

        {/* Estratégia & plano — desktop sempre; mobile só ao entrar pelo card */}
        {(!isMobile || mobileNav === 'estrategia') && (<>
        {isMobile && (
          <button onClick={() => setMobileNav('hub')} className="flex items-center gap-1.5 text-sm text-ink-muted font-medium -mb-1">
            <span aria-hidden>←</span> Voltar
          </button>
        )}

        {/* Sub-abas: Plano & estratégia · Explorar (curadoria manual, ex-Sugestão) */}
        <div className="flex gap-1 bg-sunken border border-line rounded-xl p-1 w-fit">
          {(['plano', 'explorar'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === v ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}
            >
              {v === 'plano' ? '🎯 Plano & estratégia' : '🔎 Explorar itens'}
            </button>
          ))}
        </div>

        {view === 'explorar' && <PurchaseExplorer />}

        {view === 'plano' && (<>
        {/* Cap mensal + colchão por curva ABC — parâmetros da regra, editáveis aqui */}
        <OrcamentoMensalCard orders={orders} monthKey={monthKey} />
        <SafetyStockCard />

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && (
          <>
            {/* Estratégia anti-ruptura para a meta de ≤5% */}
            <div className="bg-surface border border-line rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">
                🎯 Estratégia anti-ruptura · meta ≤ {Math.round(RUPTURA_TARGET * 100)}%
              </p>
              <p className="text-sm text-ink leading-snug mb-3">{rupturaStrategy.strat.headline}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <RupturaForecastCard forecast={rupturaStrategy.baseline} title="Com pedidos atuais · sem novas compras" showCta={false} />
                <RupturaForecastCard forecast={rupturaStrategy.withFits} title="Com o plano · reposição contínua" showCta={false} />
              </div>
              {(rupturaStrategy.strat.precisaAlemDoTeto || rupturaStrategy.strat.semFornecedorCount > 0 || rupturaStrategy.strat.semLeadCount > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  {rupturaStrategy.strat.precisaAlemDoTeto && (
                    <button
                      onClick={aprovarAlemDoTeto}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-800 dark:text-amber-300 font-semibold disabled:opacity-50"
                      title="Aprovar os itens que não cabem no teto do mês mas são necessários para chegar à meta de ruptura"
                    >
                      Aprovar {rupturaStrategy.strat.alemDoTetoCount} {rupturaStrategy.strat.alemDoTetoCount === 1 ? 'item' : 'itens'} acima do teto ({fmtBRL(rupturaStrategy.strat.alemDoTetoValue)})
                    </button>
                  )}
                  {rupturaStrategy.strat.semFornecedorCount > 0 && (
                    <button
                      onClick={() => navigate('/fornecedor?tab=vincular')}
                      className="px-3 py-1.5 rounded-lg bg-sunken hover:bg-line border border-line text-ink-muted"
                    >
                      {rupturaStrategy.strat.semFornecedorCount} em ruptura sem fornecedor → vincular
                    </button>
                  )}
                  {rupturaStrategy.strat.semLeadCount > 0 && (
                    <button
                      onClick={() => navigate('/fornecedor?tab=vincular')}
                      className="px-3 py-1.5 rounded-lg bg-sunken hover:bg-line border border-line text-ink-muted"
                    >
                      {rupturaStrategy.strat.semLeadCount} sem lead time → cadastrar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Reforço de ruptura: itens zerados com pedido enviado abaixo do dobro */}
            {reforco.itens.length > 0 && (
              <div className="bg-surface border border-rose-300 dark:border-rose-500/30 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-rose-50 dark:bg-rose-500/10 border-b border-rose-200 dark:border-rose-500/30">
                  <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                    🔁 Reforço de ruptura — {reforco.itens.length} {reforco.itens.length === 1 ? 'item' : 'itens'} · {fmtBRL(reforco.totalValor)}
                  </p>
                  <p className="text-[11px] text-rose-800/80 dark:text-rose-300/80 mt-0.5">
                    Itens zerados cujo pedido já enviado ficou abaixo do dobro — re-rompem dentro do lead time. Considere um pedido complementar de reforço (qtd abaixo); some quando os pedidos forem recebidos.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                        <th className="px-3 py-2">Produto</th>
                        <th className="px-3 py-2">Fornecedor</th>
                        <th className="px-3 py-2 text-right w-20">Enviado</th>
                        <th className="px-3 py-2 text-right w-20">Alvo 2×</th>
                        <th className="px-3 py-2 text-right w-20">Reforço</th>
                        <th className="px-3 py-2 text-right w-24">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reforco.itens.map(it => (
                        <tr key={it.id} className="border-b border-line/50 last:border-0">
                          <td className="px-3 py-2 text-ink">
                            <p className="truncate" title={it.name}>{it.name}</p>
                            <p className="text-[11px] text-ink-faint font-mono">{it.sku}</p>
                          </td>
                          <td className="px-3 py-2 text-ink-muted truncate" title={it.fornecedor_nome}>{it.fornecedor_nome}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{fmtInt(it.open_qty)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{fmtInt(it.alvoDobrado)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700 dark:text-rose-300">+{fmtInt(it.reforco)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink">{fmtBRL(it.valorReforco)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Regra anti-ruptura: itens zerados entram com o dobro da quantidade */}
            <p className="text-[11px] text-ink-faint px-1">
              💡 Itens <strong className="text-ink-muted">em ruptura</strong> entram com a <strong className="text-ink-muted">quantidade dobrada</strong> — comprar a sugestão simples re-rompe dentro do lead time. O ajuste manual da quantidade sobrepõe.
            </p>

            {/* Resumo do plano */}
            <div className="bg-surface border border-line rounded-xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Plano de compra</p>
                <p className="text-2xl font-bold tabular-nums text-ink leading-tight">
                  {totalItens} <span className="text-sm font-normal text-ink-muted">{totalItens === 1 ? 'item' : 'itens'} em {plano.fits.length} {plano.fits.length === 1 ? 'fornecedor' : 'fornecedores'}</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Valor total</p>
                <p className="text-2xl font-bold tabular-nums text-ink leading-tight">{fmtBRL(plano.totalFits)}</p>
              </div>
              {capAtivo && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Custo do plano (mês)</p>
                  <p className="text-2xl font-bold tabular-nums text-ink leading-tight">{fmtBRL(plano.impactoCaixaMes)}</p>
                </div>
              )}
              {totalOverflowItens > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">Para virada do mês</p>
                  <p className="text-2xl font-bold tabular-nums text-amber-700 dark:text-amber-300 leading-tight">{totalOverflowItens} itens · {fmtBRL(plano.totalOverflows)}</p>
                </div>
              )}
              <div className="ml-auto flex flex-wrap gap-2">
                {autoApproval ? (
                  <button
                    onClick={aprovarSeguros}
                    disabled={busy || segurosCount === 0}
                    className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-1.5"
                    title="Aprova apenas os itens seguros; as exceções ficam para revisão manual abaixo."
                  >
                    {busy ? '...' : '✓'} Aprovar seguros ({segurosCount})
                  </button>
                ) : (
                  <button
                    onClick={aprovarTodos}
                    disabled={busy || totalItens === 0}
                    className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-1.5"
                  >
                    {busy ? '...' : '✓'} Aprovar plano ({totalItens})
                  </button>
                )}
              </div>
            </div>

            {/* Alertas determinísticos */}
            {plano.alertas.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 dark:bg-amber-500/10 dark:border-amber-500/30">
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1">⚠ Atenção</p>
                <ul className="text-sm text-amber-900 dark:text-amber-200 space-y-0.5">
                  {plano.alertas.map((a, i) => <li key={i}>• {a}</li>)}
                </ul>
              </div>
            )}

            {/* Fase 3: exceções que precisam de revisão humana (sob a flag) */}
            {autoApproval && autoPartition && totalItens > 0 && (
              <div className="bg-surface border border-amber-300 dark:border-amber-500/30 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-300 dark:border-amber-500/30">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    ⚠️ Exceções — revisão manual ({excecoesCount})
                  </p>
                  <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80 mt-0.5">
                    {segurosCount > 0
                      ? `Os ${segurosCount} itens seguros são aprovados pelo botão "Aprovar seguros" acima. Estes fogem dos critérios e ficam com você.`
                      : 'Nenhum item se enquadrou como seguro — todos pedem sua decisão.'}
                  </p>
                </div>
                {excecoesCount === 0 ? (
                  <p className="px-4 py-4 text-sm text-emerald-600 dark:text-emerald-400">
                    🎉 Nenhuma exceção — todo o plano é seguro. Clique em "Aprovar seguros".
                  </p>
                ) : (
                  <div className="divide-y divide-line">
                    {autoPartition.excecoes.map(({ item, reasons }) => (
                      <div key={item.id} className="px-4 py-3 flex flex-wrap items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-ink text-sm truncate" title={item.name}>{item.name}</p>
                          <p className="text-[11px] text-ink-faint font-mono">{item.sku} · {item.fornecedor_nome}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {reasons.map((rs, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 font-medium">{rs}</span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-xs text-ink-muted tabular-nums shrink-0">
                          <p>Sug. <span className="text-ink font-semibold">{fmtInt(item.total_suggestion)}</span></p>
                          <p>{fmtBRL(item.total_cost)}</p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => aprovarUm(item)} disabled={busy} className="text-xs px-2.5 py-1.5 rounded-lg bg-brand-600/15 hover:bg-brand-600/25 disabled:opacity-50 text-brand-700 dark:text-brand-300 border border-brand-600/40">Aprovar</button>
                          <button onClick={() => standbyUm(item)} disabled={busy} className="text-xs px-2.5 py-1.5 rounded-lg bg-sunken hover:bg-line disabled:opacity-50 text-ink-muted border border-line">Standby</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Empty state */}
            {totalItens === 0 && totalOverflowItens === 0 && (
              <div className="bg-surface border border-line rounded-xl p-8 text-center text-ink-faint">
                <p className="text-3xl mb-2">✅</p>
                <p className="text-sm">Nenhum item precisa de reposição agora — estoques saudáveis ou todos com pedido em aberto.</p>
              </div>
            )}

            {/* Grupos que cabem */}
            {plano.fits.map(g => (
              <GrupoCard
                key={g.fornecedor_id ?? '__none__'}
                grupo={g}
                onQtyChange={(id, qty) => setQtyOverride(prev => ({ ...prev, [id]: qty }))}
                tipo="emitir"
                onAdiar={adiarGrupo}
              />
            ))}

            {/* Cap rolante: próximos meses (julho, agosto) + não cabe no horizonte */}
            {plano.overflows.length > 0 && (
              <div className="border border-line rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowOverflows(s => !s)}
                  className="w-full px-4 py-3 bg-amber-50 dark:bg-amber-500/10 flex items-center justify-between text-left"
                >
                  <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    {showOverflows ? '▼' : '▶'} Próximos meses (cap rolante) — {totalOverflowItens} {totalOverflowItens === 1 ? 'item' : 'itens'} · {fmtBRL(plano.totalOverflows)}
                  </span>
                  {showOverflows && (
                    <button
                      onClick={(e) => { e.stopPropagation(); postergarOverflows() }}
                      disabled={busy}
                      className="text-xs px-3 py-1 rounded bg-amber-200 hover:bg-amber-300 dark:bg-amber-500/30 dark:hover:bg-amber-500/40 text-amber-900 dark:text-amber-100 font-semibold disabled:opacity-50"
                    >
                      Marcar todos como Standby
                    </button>
                  )}
                </button>
                {showOverflows && (
                  <div className="bg-surface p-4 space-y-5">
                    {plano.mesesFuturos.filter(m => m.grupos.length > 0).map(mes => (
                      <div key={mes.monthKey}>
                        <p className="text-xs font-semibold text-ink-muted mb-2">
                          <span className="capitalize">{mes.label}</span> · disponível <span className="tabular-nums">{fmtBRL(mes.disponivel)}</span> · plano <span className="tabular-nums">{fmtBRL(mes.total)}</span>
                        </p>
                        <div className="space-y-3">
                          {mes.grupos.map(g => (
                            <GrupoCard
                              key={mes.monthKey + (g.fornecedor_id ?? '__none__')}
                              grupo={g}
                              onQtyChange={(id, qty) => setQtyOverride(prev => ({ ...prev, [id]: qty }))}
                              tipo="virada"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    {plano.naoCabe.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">
                          Não cabe no horizonte (até {plano.mesesFuturos[plano.mesesFuturos.length - 1]?.label}) — pedem negociação de prazo ou revisão do teto
                        </p>
                        <div className="space-y-3">
                          {plano.naoCabe.map(g => (
                            <GrupoCard
                              key={'nc' + (g.fornecedor_id ?? '__none__')}
                              grupo={g}
                              onQtyChange={(id, qty) => setQtyOverride(prev => ({ ...prev, [id]: qty }))}
                              tipo="virada"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </>)}
        </>)}
      </div>
      <MiguelChatDrawer panel={panelSnapshot} />
    </AppLayout>
  )
}

function GrupoCard({
  grupo, onQtyChange, tipo, onAdiar,
}: {
  grupo: PlanoGrupo
  onQtyChange: (id: string, qty: number) => void
  tipo: 'emitir' | 'virada'
  /** Fase 4: handler para "fornecedor recusou → adiar grupo" (só nos fits). */
  onAdiar?: (g: PlanoGrupo) => void
}) {
  const toneBorder = tipo === 'emitir' ? 'border-line' : 'border-amber-300 dark:border-amber-500/30'
  const isMobile = useIsMobile()
  return (
    <div className={`bg-surface border ${toneBorder} rounded-xl overflow-hidden`}>
      <div className="px-4 py-3 border-b border-line flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-ink font-semibold">{grupo.fornecedor_nome}</h3>
          <p className="text-xs text-ink-faint mt-0.5">
            {grupo.rows.length} {grupo.rows.length === 1 ? 'item' : 'itens'}
            {grupo.lead_time_days != null && <> · lead time <strong>{grupo.lead_time_days}d</strong></>}
            {grupo.min_order != null && (
              <> · mín. {fmtBRL(grupo.min_order)} {grupo.min_atingido ? <span className="text-emerald-600 dark:text-emerald-400">✓</span> : <span className="text-amber-600 dark:text-amber-400">faltam {fmtBRL(grupo.min_order - grupo.subtotal)}</span>}</>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-ink-faint">Subtotal</p>
          <p className="text-lg font-bold tabular-nums text-ink">{fmtBRL(grupo.subtotal)}</p>
          {grupo.impacto_caixa_mes !== grupo.subtotal && (
            <p className="text-[10px] text-ink-faint">Caixa do mês: <span className="font-semibold">{fmtBRL(grupo.impacto_caixa_mes)}</span></p>
          )}
        </div>
      </div>
      {isMobile ? (
        <div className="divide-y divide-line">
          {grupo.rows.map(r => <MobileItemCard key={r.id} item={r} onQtyChange={onQtyChange} virada={tipo === 'virada'} />)}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                <th className="px-3 py-2 w-20">Prio</th>
                <th className="px-3 py-2">Produto</th>
                <th className="px-3 py-2 text-right w-20">Estq.</th>
                <th className="px-3 py-2 text-right w-20">Sug.</th>
                <th className="px-3 py-2 text-right w-20">Qtd</th>
                <th className="px-3 py-2 text-right w-24">Custo</th>
                <th className="px-3 py-2 text-right w-28">Total</th>
              </tr>
            </thead>
            <tbody>
              {grupo.rows.map(r => <ItemRow key={r.id} item={r} onQtyChange={onQtyChange} virada={tipo === 'virada'} />)}
            </tbody>
          </table>
        </div>
      )}
      {tipo === 'emitir' && onAdiar && <NegociacaoFooter grupo={grupo} onAdiar={onAdiar} />}
    </div>
  )
}

/**
 * Fase 4 (negociação assistida): rodapé do grupo do fornecedor com um link wa.me
 * pré-montado para pedir melhor condição e o atalho "recusou → adiar p/ virada".
 * Sem API externa — o comprador envia e registra o desfecho.
 */
function NegociacaoFooter({ grupo, onAdiar }: { grupo: PlanoGrupo; onAdiar: (g: PlanoGrupo) => void }) {
  const supplier = grupo.rows[0]?.suppliers ?? null
  const [prazo, setPrazo] = useState(45)
  const link = supplier
    ? buildWhatsAppNegotiationLink({ supplier, subtotal: grupo.subtotal, prazoDiasDesejado: prazo, mode: 'prazo' })
    : null
  return (
    <div className="px-4 py-2.5 border-t border-line bg-sunken/40 flex flex-wrap items-center gap-2">
      <span className="text-[11px] text-ink-faint">💬 Negociar condição:</span>
      <label className="flex items-center gap-1 text-xs text-ink-muted">
        prazo
        <input
          type="number" min={0}
          value={prazo}
          onChange={e => setPrazo(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
          className="w-16 bg-paper border border-line rounded px-2 py-1 text-right tabular-nums text-ink"
        />
        dias
      </label>
      {link ? (
        <a
          href={link} target="_blank" rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-lg bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#0b7a52] dark:text-[#25D366] border border-[#25D366]/40 font-semibold"
          title="Abre o WhatsApp com uma mensagem de negociação de prazo pré-montada"
        >
          Negociar no WhatsApp
        </a>
      ) : (
        <span className="text-[11px] text-ink-faint italic">fornecedor sem telefone cadastrado</span>
      )}
      <button
        onClick={() => onAdiar(grupo)}
        className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-300 border border-amber-500/40 font-semibold"
        title="Fornecedor recusou a condição: adia todos os itens deste fornecedor para a virada (Standby)"
      >
        Recusou → adiar p/ virada
      </button>
    </div>
  )
}

/** Chips de regra: explicam por que o item entrou no plano e como foi priorizado. */
function RuleChips({ item, virada }: { item: PlanoItem; virada: boolean }) {
  const chips = explainPlanoItem({
    priority: item.priority,
    em_risco: item.em_risco,
    abc_curve: item.abc_curve,
    current_stock: item.current_stock,
    dobrado: isRupturaAcionavel(item.current_stock, item.kanban_status),
    virada,
  })
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {chips.map((c, i) => (
        <span key={i} title={c.why} className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded ${CHIP_TONE_CLASS[c.tone]}`}>
          {c.label}
        </span>
      ))}
    </div>
  )
}

function ItemRow({ item, onQtyChange, virada }: { item: PlanoItem; onQtyChange: (id: string, qty: number) => void; virada: boolean }) {
  const hasFilial = (item.filial_suggestion ?? 0) > 0
  return (
    <tr className="border-b border-line/50 last:border-0">
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[item.priority] ?? 'bg-gray-300 text-gray-900'}`}>
          {item.priority}
        </span>
        {isRupturaAcionavel(item.current_stock, item.kanban_status) && (
          <span
            className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30"
            title="Item em ruptura: quantidade dobrada para não re-romper dentro do lead time. Edite a quantidade para ajustar."
          >
            ×2
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-ink">
        <p className="truncate" title={item.name}>{item.name}</p>
        <p className="text-[11px] text-ink-faint font-mono">{item.sku}</p>
        <RuleChips item={item} virada={virada} />
      </td>
      <td className={`px-3 py-2 text-right tabular-nums ${item.current_stock <= 0 ? 'text-danger font-semibold' : 'text-ink-muted'}`}>{fmtInt(item.current_stock)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
        {fmtInt(item.total_suggestion)}
        {hasFilial && (
          <p className="text-[10px] text-ink-faint mt-0.5 leading-none" title="Matriz + Filiais">
            M{fmtInt(item.purchase_suggestion)} + F{fmtInt(item.filial_suggestion)}
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <input
          type="number" min={0}
          defaultValue={item.total_suggestion}
          onBlur={e => {
            const n = Math.max(0, Math.floor(Number(e.target.value) || 0))
            if (n !== item.total_suggestion) onQtyChange(item.id, n)
          }}
          className="w-16 bg-paper border border-line rounded px-2 py-1 text-right tabular-nums text-sm text-ink"
        />
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{fmtBRL(item.cost)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-ink font-medium">{fmtBRL(item.total_cost)}</td>
    </tr>
  )
}

/** Versão mobile da linha do plano: card empilhado (evita tabela com scroll lateral). */
function MobileItemCard({ item, onQtyChange, virada }: { item: PlanoItem; onQtyChange: (id: string, qty: number) => void; virada: boolean }) {
  const hasFilial = (item.filial_suggestion ?? 0) > 0
  return (
    <div className="px-3.5 py-3 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[item.priority] ?? 'bg-gray-300 text-gray-900'}`}>{item.priority}</span>
          {isRupturaAcionavel(item.current_stock, item.kanban_status) && (
            <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30" title="Item em ruptura: quantidade dobrada para não re-romper no lead time.">×2</span>
          )}
        </div>
        <p className="text-sm text-ink font-medium leading-tight" title={item.name}>{item.name}</p>
        <p className="text-[11px] text-ink-faint font-mono">{item.sku}</p>
        <RuleChips item={item} virada={virada} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-ink-muted tabular-nums">
          <span>Estq <b className={item.current_stock <= 0 ? 'text-danger' : 'text-ink'}>{fmtInt(item.current_stock)}</b></span>
          <span>Sug <b className="text-ink">{fmtInt(item.total_suggestion)}</b>{hasFilial && <span className="text-ink-faint"> (M{fmtInt(item.purchase_suggestion)}+F{fmtInt(item.filial_suggestion)})</span>}</span>
          <span>Total <b className="text-ink">{fmtBRL(item.total_cost)}</b></span>
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className="text-[10px] text-ink-faint uppercase tracking-wide">Qtd</span>
        <input
          type="number" min={0}
          defaultValue={item.total_suggestion}
          onBlur={e => {
            const n = Math.max(0, Math.floor(Number(e.target.value) || 0))
            if (n !== item.total_suggestion) onQtyChange(item.id, n)
          }}
          className="w-16 bg-paper border border-line rounded-lg px-2 py-1.5 text-right tabular-nums text-sm text-ink"
        />
      </div>
    </div>
  )
}

// ── Kanban de compras (mobile) ───────────────────────────────────────────
// Colunas mapeadas a dados reais: Solicitado=plano de compra · Em cotação=pedidos
// Pendente · Aprovado=produtos "Compra Aprovada" · Em trânsito=pedidos Enviado ·
// Recebido=pedidos Recebido.
const KANBAN_COLS = [
  { key: 'solicitado', label: 'Solicitado',  accent: '#FF8A00', kind: 'plano' },
  { key: 'cotacao',    label: 'Em cotação',  accent: '#CA8A04', kind: 'order' },
  { key: 'aprovado',   label: 'Aprovado',    accent: '#16A34A', kind: 'plano' },
  { key: 'transito',   label: 'Em trânsito', accent: '#2563EB', kind: 'order' },
  { key: 'recebido',   label: 'Recebido',    accent: '#0D9488', kind: 'order' },
] as const

interface KanCard { id: string; title: string; sub: string; value: string }
type KanOrder = { id: string; status: string; total_value: number; suppliers?: { name?: string | null } | null; purchase_order_items?: unknown[] | null }
type KanRow = { kanban_status: string; suppliers?: { name?: string | null } | null }
type KanPlano = { fits: PlanoGrupo[]; totalFits: number }

function buildKanbanColumns(plano: KanPlano, orders: KanOrder[], rows: KanRow[]): Record<string, KanCard[]> {
  const ordersBy = (st: string): KanCard[] =>
    orders.filter(o => o.status === st).map(o => {
      const n = o.purchase_order_items?.length ?? 0
      return { id: o.id, title: o.suppliers?.name ?? 'Fornecedor', sub: `${n} ${n === 1 ? 'item' : 'itens'}`, value: fmtBRL(o.total_value) }
    })
  const solicitado: KanCard[] = plano.fits.map(g => ({
    id: g.fornecedor_id ?? g.fornecedor_nome,
    title: g.fornecedor_nome,
    sub: `${g.rows.length} ${g.rows.length === 1 ? 'item' : 'itens'}`,
    value: fmtBRL(g.subtotal),
  }))
  const aprovadoMap = new Map<string, number>()
  for (const r of rows) if (r.kanban_status === 'Compra Aprovada') {
    const k = r.suppliers?.name ?? 'Sem fornecedor'
    aprovadoMap.set(k, (aprovadoMap.get(k) ?? 0) + 1)
  }
  const aprovado: KanCard[] = [...aprovadoMap].map(([name, count]) => ({
    id: name, title: name, sub: `${count} ${count === 1 ? 'item' : 'itens'}`, value: '',
  }))
  return { solicitado, cotacao: ordersBy('Pendente'), aprovado, transito: ordersBy('Enviado'), recebido: ordersBy('Recebido') }
}

/** Tela do kanban (drill-in): colunas com cards tocáveis. Card de pedido → Pedidos. */
function KanbanScreen({ plano, orders, rows, onBack, onOpenPedidos, onOpenEmissao }: {
  plano: KanPlano; orders: KanOrder[]; rows: KanRow[]
  onBack: () => void; onOpenPedidos: () => void; onOpenEmissao: () => void
}) {
  const cols = buildKanbanColumns(plano, orders, rows)
  // Começa com a 1ª etapa aberta; as demais o usuário abre tocando.
  const [open, setOpen] = useState<Set<string>>(() => new Set(['solicitado']))
  const toggle = (k: string) => setOpen(prev => {
    const n = new Set(prev)
    if (n.has(k)) n.delete(k); else n.add(k)
    return n
  })
  return (
    <div className="space-y-2.5">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-ink-muted font-medium">
        <span aria-hidden>←</span> Voltar
      </button>
      <p className="text-[11px] font-bold uppercase tracking-wider text-ink-faint px-0.5">Kanban de compras — toque numa etapa para ver os itens</p>
      {KANBAN_COLS.map(col => {
        const cards = cols[col.key] ?? []
        const isOpen = open.has(col.key)
        const onCard = col.kind === 'order' ? onOpenPedidos : onOpenEmissao
        return (
          <div key={col.key} className="rounded-2xl border border-line bg-surface overflow-hidden">
            {/* Cabeçalho tocável da etapa */}
            <button
              onClick={() => toggle(col.key)}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-2.5 px-3.5 py-3 active:bg-sunken/40 touch-action-manip"
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.accent }} />
              <span className="text-sm font-bold uppercase tracking-wide" style={{ color: col.accent }}>{col.label}</span>
              <span className="text-[11px] font-semibold text-ink-muted bg-sunken border border-line rounded-full min-w-[22px] text-center px-1.5">{cards.length}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`ml-auto w-4 h-4 text-ink-faint transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Lista da etapa */}
            {isOpen && (
              <div className="px-3 pb-3 pt-2 space-y-2 border-t border-line max-h-[60vh] overflow-y-auto mobile-scroll">
                {cards.length === 0 ? (
                  <p className="text-[12px] text-ink-faint py-3 text-center">Nenhum item nesta etapa.</p>
                ) : cards.map(c => (
                  <button key={c.id} onClick={onCard} className="w-full text-left bg-sunken/40 border border-line rounded-xl p-2.5 active:bg-sunken transition-colors touch-action-manip">
                    <p className="text-[12px] font-semibold text-ink leading-snug">{c.title}</p>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <span className="text-[10.5px] text-ink-faint truncate">{c.sub}</span>
                      {c.value && <span className="text-[10.5px] text-ink font-semibold tabular-nums shrink-0">{c.value}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Card touch do hub (Kanban / Estratégia). */
function HubCard({ emoji, title, subtitle, badge, onClick }: { emoji: string; title: string; subtitle: string; badge?: number; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-left bg-surface border border-line rounded-2xl p-4 shadow-sm active:bg-sunken transition-colors touch-action-manip flex items-center gap-3">
      <span className="w-11 h-11 rounded-2xl bg-brand-500/10 grid place-items-center text-xl shrink-0">{emoji}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-ink leading-tight">{title}</p>
        <p className="text-[12px] text-ink-muted mt-0.5 leading-snug">{subtitle}</p>
      </div>
      {badge != null && badge > 0 && (
        <span className="text-[11px] font-semibold text-brand-700 dark:text-brand-300 bg-brand-500/15 rounded-full px-2 py-0.5 shrink-0">{badge}</span>
      )}
      <span className="text-ink-faint text-lg shrink-0" aria-hidden>›</span>
    </button>
  )
}

/** Hub mobile do Comprador: sugestão + cards touch (Kanban de compras, Estratégia). */
function CompradorHub({ plano, orders, rows, headline, total, onAprovar, onOpenKanban, onOpenEstrategia, busy }: {
  plano: KanPlano; orders: KanOrder[]; rows: KanRow[]
  headline: string; total: number; onAprovar: () => void; onOpenKanban: () => void; onOpenEstrategia: () => void; busy: boolean
}) {
  const cols = buildKanbanColumns(plano, orders, rows)
  const totalCards = Object.values(cols).reduce((s, c) => s + c.length, 0)
  const resumo = `${cols.solicitado.length} solicitados · ${cols.cotacao.length} em cotação · ${cols.transito.length} em trânsito`
  return (
    <div className="space-y-3">
      {plano.fits.length > 0 && (
        <div className="rounded-2xl border border-brand-500/30 bg-gradient-to-br from-brand-500/10 to-surface p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-brand-700 dark:text-brand-300">
            <span className="w-5 h-5 rounded-md bg-brand-500 text-white grid place-items-center text-[11px]">✦</span>
            Sugestão do Comprador
          </div>
          <p className="text-[15px] font-semibold text-ink mt-2 leading-snug">{headline}</p>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xl font-bold tabular-nums text-ink">{fmtBRL(total)}</span>
            <button onClick={onAprovar} disabled={busy} className="ml-auto px-4 h-9 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-semibold text-sm disabled:opacity-50">
              {busy ? '...' : 'Aprovar'}
            </button>
            <button onClick={onOpenEstrategia} className="px-3.5 h-9 rounded-lg border border-line text-ink font-medium text-sm hover:bg-sunken">Ajustar</button>
          </div>
        </div>
      )}

      <HubCard emoji="🗂️" title="Kanban de compras" subtitle={resumo} badge={totalCards} onClick={onOpenKanban} />
      <HubCard emoji="🎯" title="Estratégia & plano" subtitle="Cap mensal, anti-ruptura e plano detalhado" onClick={onOpenEstrategia} />
    </div>
  )
}
